import React from 'react';

import { ErrorMessage } from '@eika/attention-message';
import { IconTypes } from '@eika/icon';

import usePaymentLimits from '../../../../fetchHooks/usePaymentLimits';
import { usePaymentAccountContext } from '../../../../hooks/useAccountState';
import useTranslation from '../../../../hooks/useTranslation';
import { NoteTypes } from '../../../../translations/translationType';
import { AmountBox, AmountInput } from '../../../AmountBox';
import { NoteField } from '../../../NoteField';
import { AccountFreeWithdrawalLabel } from '../../../shared/AccountFreeWithdrawalLabel';
import { DateField } from '../../../shared/DateField';
import { FormFieldset } from '../../../shared/FormFieldset';
import { SelectList } from '../../../shared/SelectList';
import AccountField from '../../AccountField';
import ReceiverField from '../../Payment/ReceiverField';
import { SelectedReceiver } from '../../SelectedReceiver';
import { RecurringPaymentActionType } from '../form/actions';
import { amountLimitWarning, isNoCoverage } from '../form/validate';
import FieldProps from './FieldProps';

const RecurringPaymentMobileFields: React.FC<FieldProps> = ({
    form,
    signingOperationPending,
    locked,
    shouldFocusFirstElement,
    freeWithdrawals,
    date,
    validAccounts,
    invalidAccounts,
    accountState,
}) => {
    const { state, errors, handleChange, curryChange } = form;
    const translated = useTranslation();
    const paymentLimits = usePaymentLimits();
    const { targetFromAccount } = usePaymentAccountContext();
    return (
        <FormFieldset disabled={signingOperationPending}>
            <AmountBox
                isNoCoverage={isNoCoverage(state, targetFromAccount)}
                limitWarning={amountLimitWarning(translated, state, paymentLimits.data)}
                limitError={errors.amountLimit}
                locked={locked}
                error={errors.amount}
            >
                <AmountInput
                    value={state.amount}
                    ariaLabel={translated.payAmount}
                    onChange={curryChange(RecurringPaymentActionType.AMOUNT)}
                    locked={locked}
                    focusFirst={shouldFocusFirstElement}
                />
            </AmountBox>
            <SelectList>
                <li>
                    <AccountField
                        id="recurringPaymentFromAccount"
                        buttonLabel={translated.from}
                        title={translated.paymentFrom}
                        accountNumber={state.fromAccount}
                        account={targetFromAccount}
                        accounts={validAccounts ?? []}
                        invalidAccounts={invalidAccounts}
                        error={errors.accountId}
                        onChange={curryChange(RecurringPaymentActionType.FROM_ACCOUNT_SELECTED)}
                        isLoading={accountState.isLoading}
                        disabled={signingOperationPending}
                        loadingError={accountState.error}
                        refresh={accountState.refetch}
                        showModal={state.showFromAccounts}
                        setShowModal={(showModal: boolean) => {
                            handleChange({
                                type: showModal
                                    ? RecurringPaymentActionType.SHOW_FROM_ACCOUNTS
                                    : RecurringPaymentActionType.HIDE_FROM_ACCOUNTS,
                            });
                        }}
                        placeholder={translated.payFromAccount}
                        locked={locked}
                        bottomLabel={
                            freeWithdrawals.show ? (
                                <AccountFreeWithdrawalLabel freeWithdrawals={freeWithdrawals.data} />
                            ) : undefined
                        }
                    />
                </li>
                <li>
                    <ReceiverField
                        receiver={state.toAccount}
                        onChange={(receiver: SelectedReceiver) =>
                            handleChange({ type: RecurringPaymentActionType.TO_ACCOUNT_SELECTED, payload: receiver })
                        }
                        error={errors.toAccount}
                        locked={locked}
                        disabled={signingOperationPending}
                    />
                    {state?.toAccount?.kidRequired && (
                        <ErrorMessage
                            text={translated.kidRequiredError}
                            icon={IconTypes.LUKK_24}
                            className="recurring-payment-kid-requirement__error-message"
                        />
                    )}
                </li>
                <li>
                    <NoteField
                        onChange={curryChange(RecurringPaymentActionType.NOTE)}
                        onChangeOwnText={curryChange(RecurringPaymentActionType.OWNTEXT)}
                        note={state.note}
                        ownText={state.ownText}
                        selectedReceiver={state.toAccount}
                        error={errors.note}
                        locked={locked}
                        disabled={signingOperationPending}
                        labelText={translated.amountNote[NoteTypes.MESSAGE].label}
                        placeholder={translated.amountNote[NoteTypes.MESSAGE].placeholder}
                        forceDisableKid={!state?.toAccount?.kidRequired}
                    />
                </li>
                <li>
                    <DateField
                        label={translated.date}
                        minDate={date.minDate}
                        date={state.firstPaymentDate}
                        paymentInterval={date.interval}
                        onChange={curryChange(RecurringPaymentActionType.FIRST_PAYMENT_DATE)}
                        onlyBusinessDays
                        paymentAgreement
                        onChangePaymentInterval={curryChange(RecurringPaymentActionType.PAYMENT_INTERVAL)}
                        locked={locked}
                        disabled={signingOperationPending}
                        endDate={state.lastPaymentDate}
                        onSetEndDate={curryChange(RecurringPaymentActionType.LAST_PAYMENT_DATE)}
                    />
                </li>
            </SelectList>
        </FormFieldset>
    );
};

export default RecurringPaymentMobileFields;
